import { type FC } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Colors,
  type ChartOptions,
} from "chart.js";
import "chartjs-adapter-moment";
import { Bar } from "react-chartjs-2";
import moment from "moment";
import { getColorFromString } from "@/partner/shared/utils/getColorFromString";
import { formatCurrency } from "@/partner/shared/utils/formatCurrency";

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Colors
);

export interface BarChartPoint {
  x: string;
  y: number;
}
export interface BarChartSeries {
  label: string;
  displayCurrency?: string | null;
  data: BarChartPoint[];
}
export interface BarChartData {
  calendarInterval: "HOUR" | "DAY" | "WEEK" | "MONTH" | "YEAR";
  items: BarChartSeries[];
}

interface BarChartProps {
  chart: BarChartData;
  dateFrom?: string;
  dateTo?: string;
  chartHeight?: number;
  renderLabel?: (value: string) => string;
  renderTrickY?: (value: number) => string | number;
}

/**
 * Оси и таблицы используют `formatCurrency` (символ перед/после суммы по
 * коду валюты, "$1 000"), тултип графика должен форматировать так же.
 * Функция живёт в `shared/utils/formatCurrency.ts` (не в
 * `features/products/utils`, откуда её раньше реэкспортировали): boundaries
 * запрещают shared импортировать напрямую из features.
 */
function formatDisplayCurrency(value: number, currency: string): string {
  return formatCurrency(value, currency);
}

export const BarChart: FC<BarChartProps> = ({
  chart,
  dateFrom,
  dateTo,
  chartHeight = 350,
  renderLabel = (v) => v,
  renderTrickY = (v) => v,
}) => {
  const displayCurrency = chart.items[0]?.displayCurrency ?? null;

  const data = {
    datasets: chart.items.map((item) => {
      const label = renderLabel(item.label);
      return {
        label,
        backgroundColor: getColorFromString(label, { alpha: 1 }),
        hoverBackgroundColor: getColorFromString(label, { alpha: 0.7 }),
        data: item.data,
      };
    }),
  };

  const unit = chart.calendarInterval.toLowerCase() as
    | "hour"
    | "day"
    | "week"
    | "month"
    | "year";
  let min: Date | undefined;
  let max: Date | undefined;
  if (dateFrom && dateTo) {
    if (unit === "day") {
      min = moment(dateFrom).startOf("day").subtract(12, "hours").toDate();
      max = moment(dateTo).endOf("day").subtract(12, "hours").toDate();
    } else if (unit === "week") {
      min = moment(dateFrom).startOf("month").subtract(3, "days").toDate();
      max = moment(dateTo).endOf("month").subtract(3, "days").toDate();
    } else if (unit === "month") {
      min = moment(dateFrom).startOf("month").subtract(15, "days").toDate();
      max = moment(dateTo).endOf("month").subtract(15, "days").toDate();
    } else if (unit === "year") {
      min = moment(dateFrom).startOf("year").subtract(6, "months").toDate();
      max = moment(dateTo).endOf("year").subtract(6, "months").toDate();
    }
  }

  const options: ChartOptions<"bar"> = {
    plugins: {
      legend: { display: chart.items.length > 1 },
      tooltip: {
        mode: "index",
        callbacks: {
          label(context) {
            const raw = context.raw as { y: number };
            const label = context.dataset.label
              ? `${context.dataset.label}: `
              : "";
            return displayCurrency
              ? `${label}${formatDisplayCurrency(raw.y, displayCurrency)}`
              : `${label}${renderTrickY(raw.y)}`;
          },
        },
      },
    },
    scales: {
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          // Просмотры, зрители, пользователи это целые числа: без precision
          // chart.js рисует деления 0.2, 0.4 ... на маленьких значениях.
          precision: displayCurrency ? 2 : 0,
          callback: (tickValue) => {
            const value =
              typeof tickValue === "number" ? tickValue : Number(tickValue);
            return displayCurrency
              ? formatDisplayCurrency(value, displayCurrency)
              : renderTrickY(value);
          },
        },
      },
      x: {
        stacked: true,
        type: "time",
        time: { unit },
        min: min?.valueOf(),
        max: max?.valueOf(),
        ticks: { source: "auto" },
      },
    },
    maintainAspectRatio: false,
  };

  return <Bar data={data} options={options} height={chartHeight} />;
};
